"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { usePathname } from "next/navigation";

interface LoadingContextType {
  isLoading: boolean;
  isPageReady: boolean;
}

const LoadingContext = createContext<LoadingContextType>({
  isLoading: true,
  isPageReady: false,
});

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return context;
};

export function LoadingProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isPageReady, setIsPageReady] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    
    if (isInitialLoad) {
      setIsLoading(true);
      setIsPageReady(false);

      const timer = setTimeout(() => {
        setIsLoading(false);
        
        setTimeout(() => {
          setIsPageReady(true);
        }, 100);
        setIsInitialLoad(false);
      }, 3000); 

      return () => clearTimeout(timer);
    }
  }, [isInitialLoad]);

  useEffect(() => {
    
    setIsLoading(true);
    setIsPageReady(false);

    
    const handleLoad = () => {
      setIsLoading(false);
      setIsPageReady(true);
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, [pathname]);

  useEffect(() => {
    if (isLoading) {
      
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
    } else {
      
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    }

    
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    };
  }, [isLoading]);

  return (
    <LoadingContext.Provider value={{ isLoading, isPageReady }}>
      {children}
    </LoadingContext.Provider>
  );
}
